import { z } from 'zod';

/** Кому адресовано лист — від цього залежать доступні змінні. */
export const EMAIL_CONTEXTS = ['user', 'candidate', 'employee'] as const;
export type EmailContext = (typeof EMAIL_CONTEXTS)[number];

export const EMAIL_CONTEXT_LABELS: Record<EmailContext, string> = {
  user: 'Користувач системи',
  candidate: 'Кандидат',
  employee: 'Працівник',
};

/** Системні шаблони: їх використовує сама система (запрошення, етапи конкурсу); не видаляються. */
export const EMAIL_TEMPLATE_KEYS = ['invite', 'interview', 'winner', 'rejection'] as const;
export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];

const common = [
  { key: 'org', label: 'Організація' },
  { key: 'senderName', label: 'Ім’я відправника' },
  { key: 'senderEmail', label: 'Email відправника' },
  { key: 'today', label: 'Сьогоднішня дата' },
] as const;

/** Змінні `{{key}}`, які можна вставити в шаблон, за контекстом. */
export const EMAIL_VARIABLES: Record<EmailContext, readonly { key: string; label: string }[]> = {
  user: [
    { key: 'fullName', label: 'ПІБ' },
    { key: 'firstName', label: 'Ім’я' },
    { key: 'email', label: 'Логін (email)' },
    { key: 'link', label: 'Посилання для входу' },
    { key: 'days', label: 'Скільки днів діє посилання' },
    ...common,
  ],
  candidate: [
    { key: 'fullName', label: 'ПІБ' },
    { key: 'firstName', label: 'Ім’я' },
    { key: 'lastName', label: 'Прізвище' },
    { key: 'middleName', label: 'По батькові' },
    { key: 'position', label: 'Посада' },
    { key: 'location', label: 'Місто' },
    { key: 'contest', label: 'Назва конкурсу' },
    { key: 'project', label: 'Проєкт' },
    ...common,
  ],
  employee: [
    { key: 'fullName', label: 'ПІБ' },
    { key: 'firstName', label: 'Ім’я' },
    { key: 'lastName', label: 'Прізвище' },
    { key: 'middleName', label: 'По батькові' },
    { key: 'position', label: 'Посада' },
    { key: 'project', label: 'Проєкт' },
    { key: 'workEmail', label: 'Робоча пошта' },
    ...common,
  ],
};

const VAR_RE = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** Підставляє змінні; у HTML значення екрануються. Невідомі змінні лишаються як є. */
export function fillTemplate(
  template: string,
  vars: Record<string, string | null | undefined>,
  mode: 'html' | 'text',
): string {
  return template.replace(VAR_RE, (whole, key: string) => {
    const v = vars[key];
    if (v === undefined) return whole;
    return mode === 'html' ? escapeHtml(v ?? '') : (v ?? '');
  });
}

/** Змінні, які в шаблоні вжито, але для контексту не існує (для підказки в редакторі). */
export function unknownVariables(template: string, context: EmailContext): string[] {
  const known = new Set(EMAIL_VARIABLES[context].map((v) => v.key));
  return [...new Set([...template.matchAll(VAR_RE)].map((m) => m[1]!))].filter(
    (k) => !known.has(k),
  );
}

/** Текстова версія листа з HTML редактора (для клієнтів без HTML і для пошуку). */
export function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h[1-6]|li|div)>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<a [^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, (_, href: string, text: string) =>
      text && text !== href ? `${text} (${href})` : href,
    )
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(Number(n)))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export const emailTemplateSaveSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().trim().min(1, 'Обов’язкове поле').max(200),
  context: z.enum(EMAIL_CONTEXTS),
  subject: z.string().trim().min(1, 'Обов’язкове поле').max(300),
  body: z.string().min(1, 'Обов’язкове поле').max(100_000),
});
export type EmailTemplateSaveInput = z.input<typeof emailTemplateSaveSchema>;

/** До кого лист: одна з карток (від неї беруться змінні й історія) або довільна адреса. */
export const emailTargetSchema = z.object({
  applicationId: z.uuid().optional(),
  candidateId: z.uuid().optional(),
  employeeId: z.uuid().optional(),
});

export const emailComposeSchema = emailTargetSchema.extend({ templateId: z.uuid() });

export const emailSendSchema = emailTargetSchema.extend({
  templateId: z.uuid().optional(),
  to: z.email('Некоректний email').max(255),
  subject: z.string().trim().min(1, 'Вкажіть тему').max(300),
  html: z.string().min(1, 'Порожній лист').max(200_000),
});
export type EmailSendInput = z.input<typeof emailSendSchema>;

/** Типові тексти системних шаблонів (створюються, якщо їх ще немає). */
export const DEFAULT_EMAIL_TEMPLATES: {
  key: EmailTemplateKey;
  name: string;
  context: EmailContext;
  subject: string;
  body: string;
}[] = [
  {
    key: 'invite',
    name: 'Запрошення в систему',
    context: 'user',
    subject: 'Запрошення до системи {{org}}',
    body: '<p>Вітаємо, {{fullName}}!</p><p>Для вас створено обліковий запис у системі {{org}}. Щоб увійти, задайте пароль за посиланням:</p><p><a href="{{link}}">Задати пароль і увійти</a></p><p>Логін — ця адреса: {{email}}. Посилання дійсне {{days}} дн. і спрацьовує один раз.</p>',
  },
  {
    key: 'interview',
    name: 'Запрошення на співбесіду',
    context: 'candidate',
    subject: 'Запрошення на співбесіду: {{position}}, {{location}}',
    body: '<p>Добрий день, {{firstName}}!</p><p>Дякуємо за інтерес до вакансії «{{position}}» ({{location}}). Запрошуємо вас на співбесіду.</p><p>Дата і час: …<br>Місце / посилання: …</p><p>Будь ласка, підтвердьте участь у відповіді на цей лист.</p><p>З повагою,<br>{{senderName}}<br>{{org}}</p>',
  },
  {
    key: 'winner',
    name: 'Лист переможцю',
    context: 'candidate',
    subject: 'Результати конкурсу: {{position}}',
    body: '<p>Добрий день, {{firstName}}!</p><p>Раді повідомити, що за результатами конкурсу на посаду «{{position}}» ({{location}}) комісія обрала вашу кандидатуру.</p><p>Найближчим часом ми зв’яжемося з вами щодо дати виходу та документів для оформлення.</p><p>З повагою,<br>{{senderName}}<br>{{org}}</p>',
  },
  {
    key: 'rejection',
    name: 'Відмова кандидату',
    context: 'candidate',
    subject: 'Результати конкурсу: {{position}}',
    body: '<p>Добрий день, {{firstName}}!</p><p>Дякуємо за участь у конкурсі на посаду «{{position}}» ({{location}}) і за час, який ви приділили співбесіді.</p><p>Цього разу комісія обрала іншого кандидата. Ми збережемо ваше резюме і зв’яжемося, якщо з’явиться відповідна вакансія.</p><p>З повагою,<br>{{senderName}}<br>{{org}}</p>',
  },
];
